import { Box, Button, Text } from "@mantine/core";
import { modals } from "@mantine/modals";
import { ClockIcon } from "@phosphor-icons/react";
import dayjs from "dayjs";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { IAmendmentPending } from "../../apis/invoice";
import { currencyFormat } from "../../utils";
import {
  computeAmendmentLineChanges,
  type LineChange,
} from "./amendment-line-diff";
import classes from "./AmendmentBanner.module.css";

type AmendmentBannerProps = {
  pending: IAmendmentPending;
  mode?: "seller" | "buyer";
  showWithdraw?: boolean;
  withdrawing?: boolean;
  onWithdraw?: () => void;
  /** Buyer accept — existing handler; banner only provides chrome. */
  showAccept?: boolean;
  accepting?: boolean;
  onAccept?: () => void;
  showReject?: boolean;
  onReject?: () => void;
  paidAmount?: number;
  /** When invoice is CLOSED and accept would reopen. */
  invoiceStatus?: string;
  currentLines?: any[];
  currentAdditionalLines?: any[];
  /** Proposed-by display name; omitted when empty. */
  proposedByName?: string | null;
  /** Switch items card to proposal mode (+N өөрчлөлт). */
  onShowProposal?: () => void;
};

function formatProposedAt(iso: string): string {
  const d = dayjs(iso);
  return d.isValid() ? d.format("YYYY-MM-DD HH:mm") : iso;
}

function formatSigned(amount: number): string {
  const abs = currencyFormat(Math.abs(amount));
  if (amount > 0) return `+${abs}`;
  if (amount < 0) return `−${currencyFormat(Math.abs(amount))}`;
  return currencyFormat(0);
}

function changeDetail(c: LineChange, t: (k: string, p?: any) => string): string {
  if (c.kind === "changed") {
    const parts: string[] = [];
    if (c.qtyFrom !== c.qtyTo) {
      parts.push(t("amend.changeQty", { from: c.qtyFrom, to: c.qtyTo }));
    }
    if (c.priceFrom !== c.priceTo) {
      parts.push(
        t("amend.changePrice", {
          from: currencyFormat(c.priceFrom ?? 0, { withSymbol: false }),
          to: currencyFormat(c.priceTo ?? 0, { withSymbol: false }),
        }),
      );
    }
    return parts.join(" · ") || c.name;
  }
  if (c.kind === "added" || c.kind === "removed") {
    return t("amend.changeUnitLine", {
      qty: c.qty ?? 0,
      price: currencyFormat(c.price ?? 0, { withSymbol: false }),
    });
  }
  return "";
}

export function AmendmentBanner({
  pending,
  mode = "seller",
  showWithdraw = false,
  withdrawing = false,
  onWithdraw,
  showAccept = false,
  accepting = false,
  onAccept,
  showReject = false,
  onReject,
  paidAmount = 0,
  invoiceStatus,
  currentLines = [],
  currentAdditionalLines = [],
  proposedByName,
  onShowProposal,
}: AmendmentBannerProps) {
  const { t } = useTranslation(["invoice", "common"]);

  const changes = useMemo(
    () => computeAmendmentLineChanges(pending, currentLines, currentAdditionalLines),
    [pending, currentLines, currentAdditionalLines],
  );

  const visible = changes.slice(0, 3);
  const extra = Math.max(0, changes.length - 3);
  const delta = Number(pending.newTotal) - Number(pending.previousTotal);
  const remainIfAccept = Math.max(0, Number(pending.newTotal) - Number(paidAmount || 0));
  const willReopen = invoiceStatus === "CLOSED" && remainIfAccept > 0;
  const staleStored =
    mode === "seller" &&
    pending.storedNewTotal != null &&
    Math.abs(Number(pending.storedNewTotal) - Number(pending.newTotal)) > 0.005;

  const confirmWithdraw = () => {
    if (!onWithdraw) return;
    modals.openConfirmModal({
      title: t("amend.withdrawConfirmTitle"),
      children: <Text size="sm">{t("amend.withdrawConfirmBody")}</Text>,
      labels: { confirm: t("amend.withdrawProposal"), cancel: t("common:cancel") },
      confirmProps: { color: "red" },
      onConfirm: () => onWithdraw(),
    });
  };

  const pillClass = (kind: LineChange["kind"]) => {
    if (kind === "added") return `${classes.pill} ${classes.pillAdded}`;
    if (kind === "removed") return `${classes.pill} ${classes.pillRemoved}`;
    return `${classes.pill} ${classes.pillChanged}`;
  };

  const pillLabel = (kind: LineChange["kind"]) => {
    if (kind === "added") return t("amend.lineNew");
    if (kind === "removed") return t("amend.lineRemoved");
    return t("amend.lineChanged");
  };

  return (
    <Box className={classes.card}>
      <Box className={classes.head}>
        <Box className={classes.headLeft}>
          <Box className={classes.iconBox} aria-hidden>
            <ClockIcon size={18} weight="bold" />
          </Box>
          <Box>
            <Text className={classes.headTitle}>{t("amend.pendingCardTitle")}</Text>
            <Text className={classes.headSub}>
              <Text component="span" className={classes.headSubMono}>
                {pending.refCode}
              </Text>
              {" · "}
              {t("amend.pendingSentAt", { at: formatProposedAt(pending.proposedAt) })}
              {proposedByName ? ` · ${proposedByName}` : null}
            </Text>
          </Box>
        </Box>

        <Box className={classes.headActions}>
          {mode === "seller" && showWithdraw && onWithdraw && (
            <Button
              variant="default"
              classNames={{ root: classes.withdrawBtn }}
              loading={withdrawing}
              onClick={confirmWithdraw}
            >
              {t("amend.withdrawProposal")}
            </Button>
          )}
          {mode === "buyer" && showReject && onReject && (
            <Button
              variant="default"
              classNames={{ root: classes.withdrawBtn }}
              onClick={onReject}
            >
              {t("amend.reject")}
            </Button>
          )}
          {mode === "buyer" && showAccept && onAccept && (
            <Button
              color="brand"
              classNames={{ root: classes.acceptBtn }}
              loading={accepting}
              onClick={onAccept}
            >
              {t("amend.accept")}
            </Button>
          )}
        </Box>
      </Box>

      <Box className={classes.body}>
        <Box>
          <Text className={classes.colLabel}>{t("amend.totalCol")}</Text>
          <Text className={classes.oldTotal}>{currencyFormat(pending.previousTotal)}</Text>
          <Text className={classes.newTotal}>{currencyFormat(pending.newTotal)}</Text>
          <Text className={`${classes.delta} ${delta < 0 ? classes.deltaNeg : ""}`}>
            {t("amend.deltaLabel", { value: formatSigned(delta) })}
          </Text>
        </Box>

        <Box>
          <Text className={classes.colLabel}>{t("amend.remainIfAccept")}</Text>
          <Text className={classes.newTotal}>{currencyFormat(remainIfAccept)}</Text>
          <Text className={classes.hint}>
            {t("amend.paidStays", { amount: currencyFormat(paidAmount || 0) })}
          </Text>
          {willReopen && (
            <Text className={classes.reopenHint}>{t("amend.willReopen")}</Text>
          )}
        </Box>

        <Box>
          <Text className={classes.colLabel}>{t("amend.reasonLabelShort")}</Text>
          <Box className={classes.reasonBox}>{pending.reason || "-"}</Box>
        </Box>
      </Box>

      {staleStored && (
        <Text className={classes.staleNotice}>
          {t("amend.staleTotalNotice", {
            stored: currencyFormat(Number(pending.storedNewTotal)),
            recomputed: currencyFormat(Number(pending.newTotal)),
          })}
        </Text>
      )}

      {changes.length > 0 && (
        <Box className={classes.changes}>
          <Text className={classes.changesHead}>
            {t("amend.changesHead", { count: changes.length })}
          </Text>
          {visible.map((c) => (
            <Box key={c.key} className={classes.changeRow}>
              <Text component="span" className={pillClass(c.kind)}>
                {pillLabel(c.kind)}
              </Text>
              <Text component="span" fw={500}>
                {c.name}
              </Text>
              <Text component="span" c="dimmed">
                {changeDetail(c, t)}
              </Text>
              <Text
                component="span"
                className={`${classes.changeDelta} ${c.delta < 0 ? classes.changeDeltaNeg : ""}`}
              >
                {t("amend.lineDelta", { value: formatSigned(c.delta) })}
              </Text>
            </Box>
          ))}
          {extra > 0 && onShowProposal && (
            <button type="button" className={classes.moreBtn} onClick={onShowProposal}>
              {t("amend.moreChanges", { count: extra })}
            </button>
          )}
        </Box>
      )}
    </Box>
  );
}
